import type { FormEvent } from "react";

export type CommunityTier = "FREE" | "INSIDER" | "CREATOR" | "LIFETIME_CREATOR";
export type CapabilityState = "READY" | "SANDBOX_ONLY" | "EXTERNAL_BLOCKER" | "DISABLED";
export type StreamVisibility = "PUBLIC" | "FOLLOWERS" | "PRIVATE";
export type StreamStatus = "SCHEDULED" | "LIVE" | "ENDED" | "EMERGENCY_STOPPED";
export type KidsAgeBand = "AGE_3_5" | "AGE_6_8" | "AGE_9_12";

export interface CommunityCapability {
  readonly key: string;
  readonly label: string;
  readonly state: CapabilityState;
  readonly reason: string | null;
}

export interface CreatorSummary {
  readonly creatorAccountId: string;
  readonly handle: string;
  readonly displayName: string;
  readonly bio: string;
  readonly followerCount: number;
  readonly followed: boolean;
  readonly liveCount: number;
}

export interface CreatorStreamSummary {
  readonly streamId: string;
  readonly creatorAccountId: string;
  readonly creatorDisplayName: string;
  readonly title: string;
  readonly category: string;
  readonly locationLabel?: string | null;
  readonly locationBucket?: string | null;
  readonly visibility: StreamVisibility;
  readonly status: StreamStatus;
  readonly startsAt: string;
  readonly offerShelf: readonly string[];
  readonly pinnedOfferReference?: string | null;
  readonly providerPlaybackUrl?: string | null;
  readonly playback?: {
    readonly streamId: string;
    readonly providerSessionReference: string;
    readonly playbackUrl: string | null;
    readonly startedAt: string;
    readonly sessionMetadataCaptured: true;
    readonly providerGenerationPerformed: false;
  } | null;
  readonly providerState: CapabilityState;
  readonly providerReason: string | null;
}

export interface CreatorAnalyticsSummary {
  readonly streamViews: number;
  readonly playbackStarts: number;
  readonly chatMessages: number;
  readonly offerClicks: number;
  readonly privateViewerIdentityStored: false;
}

export interface CreatorChatMessage {
  readonly messageId: string;
  readonly streamId?: string;
  readonly authorAccountId?: string;
  readonly authorDisplayName: string;
  readonly text: string;
  readonly status: "VISIBLE" | "HIDDEN_BY_MODERATION";
  readonly createdAt: string;
}

export interface CreatorReplaySummary {
  readonly replayId: string;
  readonly streamId: string;
  readonly title: string;
  readonly visibility: StreamVisibility;
  readonly expiresAt: string;
  readonly deleted: boolean;
}

export interface CreatorCampaignSummary {
  readonly campaignId: string;
  readonly name: string;
  readonly source: "STREAM" | "LINK" | "QR" | "OFFER";
  readonly streamId: string | null;
  readonly archivedAt: string | null;
}

export interface MemberPageSummary {
  readonly accountId: string;
  readonly handle: string;
  readonly displayName: string;
  readonly bio: string;
  readonly profileImageUrl: string | null;
  readonly coverImageUrl: string | null;
  readonly contactLinks: readonly { readonly label: string; readonly url: string; readonly kind: "EMAIL" | "PHONE" | "WEBSITE" | "SOCIAL" | "OTHER" }[];
  readonly theme: "CLEAN_LIGHT" | "CLEAN_DARK" | "TRAVEL_BLUE";
  readonly referralUrl: string;
  readonly qrCodePayload: string;
  readonly publicVisible: boolean;
  readonly searchIndexOptIn: boolean;
  readonly sponsorDiscoveryConsent: boolean;
  readonly sponsorMessageConsent: boolean;
  readonly analytics: {
    readonly pageViews: number;
    readonly referralClicks: number;
    readonly attributedSignups: number;
    readonly privateVisitorIdentityStored: false;
  };
  readonly sponsorWelcomeMessage: string | null;
}

export interface SponsorThreadSummary {
  readonly connectionId: string;
  readonly sponsorDisplayName: string;
  readonly memberDisplayName: string;
  readonly status: "PENDING" | "CONNECTED" | "BLOCKED";
  readonly unreadCount: number;
}

export interface PollSummary {
  readonly pollId: string;
  readonly question: string;
  readonly options: readonly { readonly optionId: string; readonly label: string; readonly count: number | null }[];
  readonly ownVote: string | null;
  readonly closesAt: string;
  readonly closed: boolean;
  readonly canSeeResults: boolean;
}

export interface VipSummary {
  readonly vipId: string;
  readonly title: string;
  readonly format: "AUCTION" | "REQUEST_APPROVAL" | "FIXED_PRICE" | "CHARITY";
  readonly currency: string;
  readonly closesAt: string;
  readonly remaining: number;
  readonly highestBidMinor: number | null;
  readonly fixedPriceMinor?: number | null;
  readonly requested: boolean;
  readonly paymentRequiredBeforeConfirmation?: boolean;
  readonly cancellationPolicy?: string;
  readonly beneficiary?: string | null;
}

export interface ReviewSummary {
  readonly reviewId: string;
  readonly targetTitle: string;
  readonly rating: number;
  readonly comment: string;
  readonly moderationStatus: "PENDING" | "VISIBLE" | "REMOVED";
}

export interface KidsConsentSummary {
  readonly status: "NOT_STARTED" | "PENDING" | "VERIFIED" | "EXPIRED";
  readonly expiresAt: string | null;
}

export interface KidsProfileSummary {
  readonly childProfileId: string;
  readonly alias: string;
  readonly avatarKey: string;
  readonly ageBand: KidsAgeBand;
  readonly creationCount: number;
}

export type KidsCreationResultStatus = "QUEUED_PROVIDER_EXECUTION" | "BLOCKED_PROVIDER_EXECUTION" | "APPROVED" | "FAILED_PROVIDER_EXECUTION";
export type KidsArtifactKind = "TEXT" | "IMAGE" | "VIDEO" | "AUDIO";

export interface KidsSignedArtifactAccess {
  readonly accessId: string;
  readonly creationId: string;
  readonly childProfileId: string;
  readonly kind: KidsArtifactKind;
  readonly signedUrl: string;
  readonly storageObjectReference?: string;
  readonly narrationSignedUrl?: string | null;
  readonly narrationObjectReference?: string | null;
  readonly expiresAt: string;
  readonly parentSessionScoped: true;
  readonly privateByDefault: true;
  readonly signedAccessRequired?: true;
  readonly providerGenerationPerformed?: false;
  readonly downloadable: boolean;
  readonly shareable: boolean;
  readonly readAloudAvailable: boolean;
}

export interface KidsCreationJobSummary {
  readonly jobId: string;
  readonly childProfileId: string;
  readonly kind: "STORY" | "PICTURE" | "VIDEO";
  readonly inputMode: "TYPED" | "VOICE";
  readonly status: KidsCreationResultStatus;
  readonly providerReason: string | null;
  readonly lifecycle?: readonly { readonly status: KidsCreationResultStatus; readonly occurredAt: string; readonly reason: string | null }[];
  readonly creationId?: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface KidsCreationSummary {
  readonly creationId: string;
  readonly childProfileId: string;
  readonly title: string;
  readonly kind: "STORY" | "PICTURE" | "VIDEO";
  readonly privateByDefault: true;
  readonly resultStatus?: KidsCreationResultStatus;
  readonly artifact?: KidsSignedArtifactAccess | null;
  readonly downloadAuthorized: boolean;
  readonly favorite?: boolean;
  readonly viewedAt?: string | null;
  readonly downloadedAt?: string | null;
  readonly sharedAt?: string | null;
  readonly shareAuthorizationId?: string | null;
  readonly readAloudAt?: string | null;
}

export type MaybePromise<T> = T | Promise<T>;

export function communityField(data: FormData, name: string): string {
  return String(data.get(name) ?? "").trim();
}

export function communityNumber(data: FormData, name: string): number {
  const value = Number(communityField(data, name));
  if (!Number.isSafeInteger(value) || value < 0) throw new Error(`${name} must be a non-negative integer`);
  return value;
}

export function communitySubmit(event: FormEvent<HTMLFormElement>, action: (data: FormData) => MaybePromise<void>): void {
  event.preventDefault();
  void action(new FormData(event.currentTarget));
}

export function formatCommunityDate(value: string, locale = "en"): string {
  const timestamp = Date.parse(value);
  if (!Number.isFinite(timestamp)) return value;
  return new Intl.DateTimeFormat(locale, { dateStyle: "medium", timeStyle: "short" }).format(new Date(timestamp));
}

export function formatMinorUnits(minor: number, currency: string, locale = "en"): string {
  return new Intl.NumberFormat(locale, { style: "currency", currency }).format(minor / 100);
}

export function capabilityCopy(capability: CommunityCapability | undefined): string {
  if (capability === undefined) return "Unavailable";
  if (capability.state === "READY") return "Ready";
  if (capability.state === "SANDBOX_ONLY") return "Sandbox";
  if (capability.state === "EXTERNAL_BLOCKER") return capability.reason ?? "External setup required";
  return capability.reason ?? "Disabled";
}

export function canChatInStream(stream: CreatorStreamSummary | null): boolean {
  return stream !== null && stream.status === "LIVE" && stream.providerState !== "EXTERNAL_BLOCKER";
}

export function canRequestVip(tier: CommunityTier): boolean {
  return tier !== "FREE";
}
